import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { usePagination } from "../hooks";
import {
  Box,
  Button,
  Typography,
  TextField,
  InputBase,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Skeleton,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  RestartAlt as RestartAltIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { SCREEN_IDS } from "../components/FullComponent.js";
import { translateText } from "../translations/translationService";
import { notify } from "../services/ui/notify";

import { typeConnect, backgroundColor2 } from "../constants/Constants";
import {
  getFullMo,
  insertNewWeight,
  setLog,
} from "../services/ConnectDataBase";
import { FINDINGS_WEIGHING_SCREEN_COLORS } from "../style/colors.js";
import { getCurrentDateTime, formatDate } from "../services/Functions";
import { updateListaPesos } from "../redux/reducers";

export default function FindingsWeighingScreen() {
  const [isLoading, setIsLoading] = useState(true);
  // Hook de paginación
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(0, 20);
  const scanInputRef = useRef(null);
  const dispatch = useDispatch();
  const screenName = "FindingsWeighingScreen";
  const currentScreenId = SCREEN_IDS.FINDINGS_WEIGHT;
  const tipo_tiempo = "WEIGHT";
  const lang = useSelector((state) => state.lang.value);

  const listData = useSelector((state) => state.pesosList.listaPesos);
  const usuario = useSelector((state) => state.user.name);
  const equipo = useSelector((state) => state.user.team);
  const [searchedMOs, setSearchedMOs] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [refSearch, setRefSearch] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [weights, setWeights] = useState({});
  const [isSearchActive, setIsSearchActive] = useState(false);

  const initialData = useMemo(() => {
    const initialWeights = {};
    listData.forEach((item) => {
      initialWeights[item.key] = "";
    });
    return { initialWeights };
  }, [listData]);

  useEffect(() => {
    setWeights(initialData.initialWeights);
  }, [initialData]);

  const getMo = useCallback(async () => {
    setIsLoading(true);
    try {
      const fullMo = await getFullMo(
        screenName,
        typeConnect[0],
        currentScreenId
      );

      const moArray = Object.entries(fullMo).map(([po, mo]) => ({
        key: po,
        po,
        ...mo,
      }));
      dispatch(updateListaPesos(moArray));
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    getMo();
  }, [getMo]);

  const applySearch = useCallback(
    (dataToSearch = listData) => {
      if (!isSearchActive || searchedMOs.length === 0) {
        return dataToSearch;
      }

      const results = dataToSearch.filter((item) =>
        searchedMOs.includes(item.mo)
      );
      return results;
    },
    [searchedMOs, isSearchActive, listData]
  );

  useEffect(() => {
    if (isSearchActive) {
      const newResults = applySearch();
      setSearchResults(newResults);
    }
  }, [listData, applySearch, isSearchActive]);

  const handleSearch = (searchValue) => {
    if (typeof searchValue !== "string") {
      console.error("searchValue no es una cadena");
      return;
    }

    const moArray = searchValue
      .replace(/'/g, "-")
      .split(" ")
      .map((mo) => mo.trim());

    let results = [];
    const foundMOs = [];

    moArray.forEach((mo) => {
      if (mo.length > 0) {
        const found = listData.filter((item) => item.mo === mo);
        if (found.length > 0) {
          results = results.concat(found);
          foundMOs.push(mo);
        }
      }
    });

    setSearchedMOs(foundMOs);
    setSearchResults(results);
    setIsSearchActive(results.length > 0);
    setRefSearch("");
  };

  const clearSearch = () => {
    setSearchedMOs([]);
    setSearchResults([]);
    setIsSearchActive(false);
    setRefSearch("");
  };

  const handleAdd = useCallback(
    async (item) => {
      const newWeightValue = weights[item.key];

      if (!newWeightValue || parseFloat(newWeightValue) === 0) {
        console.log("El valor de peso es inválido o cero.");
        return;
      }

      const status = await insertNewWeight(
        item.po,
        item.mo,
        usuario,
        equipo,
        item.codigo,
        newWeightValue,
        getCurrentDateTime(),
        "Fornitura.",
        tipo_tiempo,
        currentScreenId,
        item.cantidad
      );

      if (status) {
        console.log("Peso de fornitura añadido correctamente.");

        const logData = `MO: ${
          item.mo
        }, Peso: ${newWeightValue}, Fecha y Hora: ${new Date().toLocaleString()}, Usuario: ${usuario}, Equipo: ${equipo}`;
        await setLog(SCREEN_IDS.GENERAL, "LOG", "PesajeFornituras", logData);

        const updatedListData = listData.filter(
          (rowData) => rowData.key !== item.key
        );
        dispatch(updateListaPesos(updatedListData));

        setSearchResult(null);
        setWeights((prevWeights) => ({
          ...prevWeights,
          [item.key]: "",
        }));
      } else {
        notify.error(
          "Error al añadir peso. Revisa la función insertNewIntermediateWeight."
        );
      }
    },
    [weights, usuario, equipo, currentScreenId, dispatch, listData]
  );


  const rowsToShow = isSearchActive ? searchResults : listData;

  return (
    <Box sx={{ padding: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          padding: 1.5,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          marginBottom: 1.5,
        }}>
        <Box sx={{ flex: 1, minWidth: "280px", position: "relative" }}>
          <InputBase
            inputRef={scanInputRef}
            value={refSearch || ""}
            onChange={(e) => {
              let inputValue = e.target.value.replace(/'/g, "-");

              if (/^\d{6}-\d{3}$/.test(inputValue.split(" ").slice(-1)[0])) {
                inputValue += " ";
              }

              setRefSearch(inputValue);
            }}
            placeholder={translateText("Código de MO", lang)}
            sx={{
              width: "100%",
              backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_CARD || "#F9FAFB",
              color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
              fontSize: "0.85rem",
              fontWeight: 500,
              padding: "8px 12px 8px 40px",
              borderRadius: 2,
              border: `1.5px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_CARD || "#e5e7eb"}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_INPUT || "#F3F4F6",
                borderColor: FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_BRAND || "#174045",
                boxShadow: "0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "&.Mui-focused": {
                backgroundColor: "white",
                borderColor: FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_BRAND || "#174045",
                boxShadow:
                  "0 0 0 2px rgba(70, 93, 100, 0.1), 0 2px 4px rgba(70, 93, 100, 0.1)",
              },
              "& input": {
                padding: 0,
              },
              "& input::placeholder": {
                color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_MUTED || "#9CA3AF",
                opacity: 1,
              },
            }}
          />

          <Box
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_MUTED || "#9CA3AF",
              pointerEvents: "none",
            }}>
            <SearchIcon sx={{ fontSize: 18 }} />
          </Box>

          {isSearchActive && (
            <IconButton
              onClick={clearSearch}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                padding: 0.5,
                color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_SECONDARY,
                backgroundColor: "rgba(107, 114, 128, 0.1)",
                borderRadius: 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(239, 68, 68, 0.1)",
                  color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_SECONDARY,
                  transform: "translateY(-50%) scale(1.1)",
                },
              }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>

        <Button
          onClick={() => handleSearch(refSearch)}
          variant='contained'
          size="small"
          startIcon={<SearchIcon />}
          sx={{
            backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY || "#174045",
            color: "white",
            borderRadius: 2,
            fontSize: "0.8rem",
            fontWeight: 600,
            padding: "6px 16px",
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY || "#1e4d52",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(70, 93, 100, 0.2)",
            },
          }}>
          {translateText("Buscar", lang)}
        </Button>

        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: "1px",
            height: 32,
            backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_CARD || "#e5e7eb",
          }}
        />

        <Box sx={{ flex: 1, textAlign: "center" }}>
          <Typography variant='h6' sx={{ fontSize: "1rem", fontWeight: 600 }}>
            {translateText("Pesaje Fornituras", lang)}
          </Typography>
          {isSearchActive && (
            <Typography
              variant='caption'
              sx={{
                display: "block",
                color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280",
                fontSize: "0.75rem",
                mt: 0.5,
              }}>
              ({searchResults.length}{" "}
              {translateText("resultados filtrados", lang)})
            </Typography>
          )}
        </Box>

        <IconButton
          onClick={() => getMo()}
          sx={{
            padding: 1,
            color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_BRAND || "#174045",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(70, 93, 100, 0.08)",
              transform: "scale(1.1)",
            },
          }}>
          <RestartAltIcon />
        </IconButton>
      </Box>
      {isLoading ? (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <Box sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      translateText("PO", lang),
                      translateText("MO", lang),
                      translateText("Cantidad", lang),
                      translateText("Referencia", lang),
                      translateText("gr. Teo. Uni", lang),
                      translateText("gr. Teo. Total", lang),
                      translateText("Entrega", lang),
                      translateText("Peso (g)", lang),
                      translateText("Acción", lang),
                    ].map((header, i) => (
                      <TableCell key={i} sx={{ py: 1, px: 1.5 }}>
                        <Skeleton variant="text" width="100%" height={24} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(9)].map((_, j) => (
                        <TableCell key={j} sx={{ py: 0.75, px: 1.5 }}>
                          <Skeleton variant="text" width="100%" height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      ) : rowsToShow.length === 0 ? (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
            textAlign: "center",
            py: 8,
          }}>
          <Typography variant='h6' sx={{ color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280" }}>
            {translateText("No hay MOs pendientes...", lang)}
          </Typography>
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
          }}>
          <TableContainer sx={{ maxHeight: "calc(78vh - 60px)" }}>
            <Table stickyHeader size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("PO", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("MO", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Cantidad", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Referencia", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("gr. Teo. Uni", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("gr. Teo. Total", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Entrega", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Peso (g)", lang)}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_TABLE_HEADER || "#F9FAFB",
                      borderBottom: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_TABLE || "#e5e7eb"}`,
                      fontSize: "0.75rem",
                      color: "black",
                      py: 1,
                      px: 1.5,
                    }}>
                    {translateText("Acción", lang)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rowsToShow
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow
                      key={item.key}
                      sx={{
                        "&:hover": {
                          backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
                        },
                        borderBottom: `1px solid ${FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb"}`,
                      }}>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.po}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.mo}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {Number(item.cantidad).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {item.codigo}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {parseFloat(item.gramos).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {(item.gramos * item.cantidad).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{
                          fontSize: "0.8rem",
                          py: 0.75,
                          px: 1.5,
                          color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_PRIMARY || "#374151",
                        }}>
                        {formatDate(item.fechaentrega)}
                      </TableCell>
                      <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                        <TextField
                          value={weights[item.key] || ""}
                          onChange={(e) =>
                            setWeights((prev) => ({
                              ...prev,
                              [item.key]: e.target.value,
                            }))
                          }
                          type='number'
                          placeholder={translateText("Peso", lang)}
                          size='small'
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              backgroundColor: "white",
                              height: "28px",
                              width: "100px",
                              "& fieldset": {
                                borderColor: FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_LIGHT || "#e5e7eb",
                              },
                              "&:hover fieldset": {
                                borderColor: FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_BRAND || "#174045",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: FINDINGS_WEIGHING_SCREEN_COLORS.BORDER_BRAND || "#174045",
                                borderWidth: "1px",
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              textAlign: "center",
                              fontSize: "0.75rem",
                              py: 0.5,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align='center' sx={{ py: 0.75, px: 1.5 }}>
                        <Button
                          onClick={() => handleAdd(item)}
                          variant='contained'
                          sx={{
                            width: "120px",
                            padding: "4px 12px",
                            fontSize: "0.75rem",
                            height: "28px",
                            backgroundColor: backgroundColor2,
                            textTransform: "none",
                            borderRadius: 1,
                            boxShadow: "none",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_HOVER || "#1e4d52",
                              transform: "translateY(-1px)",
                              boxShadow: "0 2px 4px rgba(70, 93, 100, 0.2)",
                            },
                          }}>
                          {translateText("Registrar", lang)}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            count={rowsToShow.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              backgroundColor: FINDINGS_WEIGHING_SCREEN_COLORS.BACKGROUND_ROW_ALTERNATE || "#F9FAFB",
              "& .MuiTablePagination-toolbar": {
                fontSize: "0.75rem",
                color: FINDINGS_WEIGHING_SCREEN_COLORS.TEXT_SECONDARY || "#6B7280",
                minHeight: "52px",
              },
              "& .MuiTablePagination-select": {
                fontSize: "0.75rem",
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: "0.75rem",
              },
            }}
          />
        </Paper>
      )}
    </Box>
  );
}
